import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Button, 
  Paper, 
  Typography, 
  Alert, 
  CircularProgress, 
  Divider, 
  TextField,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment
} from '@mui/material';
import fosterLogo from '../assets/Fosterway logo white_bg.png';
import { 
  Google as GoogleIcon, 
  Apple as AppleIcon, 
  Phone as PhoneIcon,
  Email as EmailIcon,
  Visibility,
  VisibilityOff,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { activityLogService } from '../services/activityLogService';
import { referralLoggingService } from '../services/referralLoggingService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Referral tracking
  const [referringUserId, setReferringUserId] = useState<string | null>(null);

  // Email/Password state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [displayName, setDisplayName] = useState('');

  // Phone state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [canResendCode, setCanResendCode] = useState(false);

  // Reset password state
  const [resetEmail, setResetEmail] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Ref for auto-focusing the Full Name field
  const fullNameInputRef = useRef<HTMLInputElement>(null);


  useEffect(() => {
    // Extract referral parameter from URL
    const refParam = searchParams.get('ref');
    if (refParam) {
      setReferringUserId(refParam);
      console.log('Referral detected:', refParam);
    }
    
    // Check if we should show sign-up form (from referral link)
    const signupParam = searchParams.get('signup');
    if (signupParam === 'true') {
      setIsSignUp(true);
      console.log('Auto-switching to sign-up form from referral link');
      
      // Auto-focus the Full Name field after a short delay to ensure it's rendered
      setTimeout(() => {
        if (fullNameInputRef.current) {
          fullNameInputRef.current.focus();
        }
      }, 100);
    }
  }, [searchParams]);

  useEffect(() => {
    // Cleanup recaptcha on unmount
    return () => {
      authService.destroyRecaptcha();
    };
  }, []);

  // Cleanup when component unmounts or when switching away from phone tab
  useEffect(() => {
    return () => {
      authService.destroyRecaptcha();
    };
  }, [tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
    
    // Reset phone authentication state when switching away from phone tab
    if (newValue !== 0) {
      setShowPhoneVerification(false);
      setVerificationCode('');
      setPhoneNumber('');
      setCanResendCode(false);
      authService.destroyRecaptcha();
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  // Helper function to log referral signup
  const logReferralSignup = async (newUserId: string, signupMethod: 'email' | 'google' | 'apple' | 'phone') => {
    if (referringUserId) {
      try {
        await referralLoggingService.logReferralSignup({
          referringUserId,
          newUserId,
          signupMethod,
          timestamp: Date.now(),
          userAgent: navigator.userAgent,
          // Note: IP address would need to be captured server-side for security
        });
        console.log('Referral signup logged successfully');
      } catch (error) {
        console.error('Error logging referral signup:', error);
        // Don't throw here as this shouldn't block the signup process
      }
    }
  };

  // Email/Password Authentication
  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    clearMessages();

    try {
      if (isSignUp) {
        const userCredential = await authService.signUpWithEmail(email, password, displayName);
        setSuccess('Account created successfully! Welcome to FosterWay.');
        
        // Log referral signup if applicable
        await logReferralSignup(userCredential.user.uid, 'email');
      } else {
        await authService.signInWithEmail(email, password);
        setSuccess('Successfully signed in! Redirecting...');
      }
      
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Google Authentication
  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    clearMessages();

    try {
      const result = await authService.signInWithGoogle();
      
      // Check if this is a new user (first time signing in)
      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
      
      if (isNewUser) {
        // Log referral signup for new users
        await logReferralSignup(result.user.uid, 'google');
      }
      
      setSuccess(`Successfully signed in as ${result.user.displayName || result.user.email}! Redirecting...`);
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Apple Authentication
  const handleAppleSignIn = async () => {
    setIsLoading(true);
    clearMessages();

    try {
      const result = await authService.signInWithApple();
      
      // Check if this is a new user (first time signing in)
      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
      
      if (isNewUser) {
        // Log referral signup for new users
        await logReferralSignup(result.user.uid, 'apple');
      }
      
      setSuccess(`Successfully signed in as ${result.user.displayName || result.user.email}! Redirecting...`);
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Phone Authentication
  const handlePhoneSignIn = async () => {
    if (!phoneNumber) {
      setError('Please enter a phone number');
      return;
    }

    setIsLoading(true);
    clearMessages();

    try {
      await authService.signInWithPhone(phoneNumber, 'recaptcha-container');
      setShowPhoneVerification(true);
      setSuccess('Verification code sent! Please check your phone.');
      
      // Enable resend after 30 seconds
      setCanResendCode(false);
      setTimeout(() => {
        setCanResendCode(true);
      }, 30000);
    } catch (error: any) {
      setError(error.message);
      // Reset phone verification state on error
      setShowPhoneVerification(false);
      setVerificationCode('');
      
      // Show rate limiting message
      if (error.message.includes('Too many attempts') || error.message.includes('wait')) {
        setError(error.message + ' You can also try using email or social login instead.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setError('Please enter the verification code');
      return;
    }

    setIsLoading(true);
    clearMessages();

    try {
      const result = await authService.verifyPhoneCode(verificationCode);
      
      // Check if this is a new user (first time signing in)
      const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
      
      if (isNewUser) {
        // Log referral signup for new users
        await logReferralSignup(result.user.uid, 'phone');
      }
      
      setSuccess(`Successfully signed in as ${result.user.phoneNumber || 'user'}! Redirecting...`);
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error: any) {
      setError(error.message);
      // Clear verification code on error
      setVerificationCode('');
    } finally {
      setIsLoading(false);
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    if (!phoneNumber) {
      setError('Phone number is required to resend code');
      return;
    }

    setIsLoading(true);
    clearMessages();

    try {
      // Temporarily hide verification form to show reCAPTCHA
      setShowPhoneVerification(false);
      
      // Wait a moment for the UI to update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      await authService.signInWithPhone(phoneNumber, 'recaptcha-container');
      setShowPhoneVerification(true);
      setSuccess('New verification code sent! Please check your phone.');
      
      // Reset resend timer
      setCanResendCode(false);
      setTimeout(() => {
        setCanResendCode(true);
      }, 30000);
    } catch (error: any) {
      setError(error.message);
      // Show verification form again even on error
      setShowPhoneVerification(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset Password
  const handleResetPassword = async () => {
    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    clearMessages();

    try {
      await authService.resetPassword(resetEmail);
      setSuccess('Password reset email sent! Please check your inbox.');
      setResetDialogOpen(false);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'flex-start', 
      minHeight: '100vh', 
      pt: { xs: 4, sm: 8, md: 12 },
      px: 2
    }}>
      <Paper sx={{ 
        p: 4, 
        minWidth: { xs: '100%', sm: 400, md: 500 }, 
        maxWidth: 500,
        textAlign: 'center',
        boxShadow: 3
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <img 
            src={fosterLogo} 
            alt="FosterWay Logo" 
            style={{ 
              height: '60px', 
              width: 'auto',
              maxWidth: '100%'
            }} 
          />
        </Box>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Sign in to access your account
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Tabs value={tabValue} onChange={handleTabChange} centered sx={{ mb: 2 }}>
          <Tab label="Phone" />
          <Tab label="Email" />
        </Tabs>

        {/* Phone Authentication Tab */}
        <TabPanel value={tabValue} index={0}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            sx={{ mb: 2 }}
            startIcon={<GoogleIcon />}
          >
            {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Login with Google
          </Button>

          <Button
            variant="outlined"
            fullWidth
            onClick={handleAppleSignIn}
            disabled={isLoading}
            sx={{ mb: 2 }}
            startIcon={<AppleIcon />}
          >
            {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Login with Apple
          </Button>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>
          
          <TextField
            label="Phone Number"
            value={phoneNumber}
            onChange={e => setPhoneNumber(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="+1 (555) 123-4567"
            helperText="Enter your phone number with country code (e.g., +1234567890)"
          />
          
          <Box sx={{ 
            margin: '16px 0', 
            minHeight: '78px', 
            display: showPhoneVerification ? 'none' : 'flex', 
            justifyContent: 'center', 
            alignItems: 'center' 
          }}>
            <div 
              id="recaptcha-container" 
              style={{ 
                display: 'flex', 
                justifyContent: 'center',
                minHeight: '78px',
                width: '100%'
              }}
            ></div>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handlePhoneSignIn}
            disabled={isLoading}
            sx={{ mb: 2 }}
            startIcon={<PhoneIcon />}
          >
            {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Send Verification Code
          </Button>

          {showPhoneVerification && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Verification Code"
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value)}
                fullWidth
                margin="normal"
                placeholder="Enter 6-digit code"
              />
              <Button
                variant="contained"
                fullWidth
                onClick={handleVerifyCode}
                disabled={isLoading}
                sx={{ mt: 2 }}
              >
                {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                Verify Code
              </Button>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResendCode}
                  disabled={!canResendCode || isLoading}
                >
                  {canResendCode ? 'Resend Code' : 'Resend Code (30s)'}
                </Button>
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* Email/Password Tab */}
        <TabPanel value={tabValue} index={1}>
          <form onSubmit={handleEmailAuth}>
            {isSignUp && (
              <TextField
                ref={fullNameInputRef}
                label="Full Name"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                fullWidth
                margin="normal"
                required={isSignUp}
                autoFocus
              />
            )}
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isLoading}
              sx={{ mt: 2, mb: 2 }}
              startIcon={<EmailIcon />}
            >
              {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              {isSignUp ? 'Create Account' : 'Sign In'}
            </Button>
          </form>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setIsSignUp(!isSignUp)}
            >
              {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
            </Button>
            {!isSignUp && (
              <Button
                variant="text"
                size="small"
                onClick={() => setResetDialogOpen(true)}
              >
                Forgot Password?
              </Button>
            )}
          </Box>
        </TabPanel>

        <Typography variant="caption" display="block" sx={{ mt: 3, color: 'text.secondary' }}>
          By signing in, you agree to our{' '}
          <a 
            href="https://fosterway.com/terms-conditions" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            Terms of Service
          </a>
          {' '}and{' '}
          <a 
            href="https://fosterway.com/privacy" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            Privacy Policy
          </a>
        </Typography>
      </Paper>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reset Password
          <IconButton
            onClick={() => setResetDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          <TextField
            label="Email"
            type="email"
            value={resetEmail}
            onChange={e => setResetEmail(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleResetPassword} 
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Send Reset Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login; 